/**
 * Main left-nav (work surfaces) — single source of truth shared by
 * `SidebarContent.js` (desktop) and `Header.js` (mobile).
 *
 * Before this file existed the desktop sidebar and the mobile hamburger
 * each hand-rolled their own JSX of the same routes with subtly
 * different visibility rules — Listings / Orders POD-exclusion /
 * Inventory allowlist all drifted apart over time. Both surfaces now
 * read `MAIN_NAV` for the list and `useMainNavVisibility()` for the
 * gates, so any visibility change is one edit instead of two.
 *
 * Data shape, mirrors `helpers/settingsNav.js`:
 *
 *   - `leaf`: `{ id, label, icon, to, type: "leaf" }` — single
 *     destination row.
 *   - `parent`: `{ id, label, icon, type: "parent", children: [...] }`
 *     — expandable group. Children may be leaves (`{ id, label, to }`)
 *     or nested parents (`{ id, label, type: "parent", children }`).
 *   - `pin: "bottom"` — render after the main list, below a divider
 *     (Settings, Help).
 *   - `external: true` + `href` — open an absolute URL in a new tab
 *     (admin Help center). Leaf still uses `type: "leaf"`; renderers
 *     emit `<a>` instead of react-router `<Link>`.
 *
 * `icon` is a string key, not a JSX node. The renderers resolve it
 * via their own component map so this file stays pure data and is
 * trivially serializable.
 *
 * Visibility lives in `useMainNavVisibility()` below, keyed by `id`.
 * The same id is used for both leaf and parent visibility — for a
 * parent, the renderer also OR-folds its children's visibility so an
 * empty parent never renders.
 *
 * NAV.1 — Sidebar restructure: job-to-be-done groups (Customers,
 * Products, Production), Settings pinned to bottom, Marketplace nav
 * entry intentionally hidden (route stays live).
 *
 * Rollout: NAV.1 is admin-rail only (`useNav1Enabled` / `isAdminNavUser`).
 * Sellers keep `MAIN_NAV_LEGACY` (pre-NAV.1 flat rail) until a later graduate.
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";

import { activeEnterprisePlanSelector } from "../selectors/pricings";
import { getPlanTypes } from "../customHooks/user";
import {
  BIG_FROG_ENTERPRISE_ID,
  isBespokeThreadsDomain,
  isBigFrogHqSuperAdmin,
  isBigfrogDomian,
  isLocalhost,
  isNetworkFranchiseMember,
  isPrintOnDemandDomain,
  isPrintifyDomain,
  isOmniCommerceContext,
  isRiverrDomain,
} from "../utils";
import { useFeatureFlags } from "./featureFlags";
import { isStagingTenant } from "./stagingTenants";
import { useSellerQuoteRequestsEnabled } from "./sellerQuoteRequestsSetting";
import { useSellerOnboardingEnabled } from "./sellerOnboardingSetting";
import { useMarketWatchEnabled } from "./marketWatchSetting";
import {
  applyStaffPermissionVisibility,
  canInviteStaff,
  isAdminNavUser,
} from "./staffAccess";
import { applySharedAccessVisibility } from "./sharedAccessVisibility";
import { applySellerPermissionVisibility } from "./sellerAccess";
import { useSellerSubscriptionsOffered } from "../pages/sellerSubscriptions/useSellerSubscriptionsOffered";
import { isAllowlistedOmniSellerSubscriptionEmail } from "../utils/sellerSubscriptionAccess";

/** Riverr admin help center (franchise / enterprise admins). */
export const ADMIN_HELP_CENTER_URL = "https://riverr.app/help";

/** Pre-NAV.1 flat rail — sellers / non-admins until NAV.1 graduates. */
export const MAIN_NAV_LEGACY = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: "dashboard",
    to: "/dashboard",
    type: "leaf",
  },
  {
    id: "inbox",
    label: "Inbox",
    icon: "inboxAdmin",
    to: "/inbox",
    type: "leaf",
  },
  {
    id: "listings",
    label: "Listings",
    icon: "listing",
    to: "/listings",
    type: "leaf",
  },
  {
    id: "catalog",
    label: "Catalog",
    icon: "catalog",
    to: "/products/new-product",
    type: "leaf",
  },
  {
    id: "products",
    label: "Products",
    icon: "product",
    to: "/products",
    type: "leaf",
  },
  {
    id: "design-library",
    label: "Design Library",
    icon: "designLibrary",
    to: "/design-library",
    type: "leaf",
  },
  {
    id: "orders-flat",
    label: "Orders",
    icon: "order",
    type: "parent",
    children: [
      { id: "orders-flat/orders", label: "Orders", to: "/orders" },
      {
        id: "orders-flat/quote-requests",
        label: "Quote requests",
        to: "/quote-requests",
      },
      { id: "orders-flat/quotes", label: "Quotes", to: "/quotes" },
      {
        id: "orders-flat/sales-orders",
        label: "Sales Orders",
        to: "/sales-orders",
      },
    ],
  },
  {
    id: "orders-admin",
    label: "Orders",
    icon: "inboxAdmin",
    type: "parent",
    children: [
      { id: "orders-admin/orders", label: "Orders", to: "/orders" },
      {
        id: "orders-admin/late-orders",
        label: "Late Orders",
        to: "/late-orders",
      },
      {
        id: "orders-admin/split-ship-pending",
        label: "Split Ship",
        to: "/split-ship-pending",
      },
      {
        id: "orders-admin/admin-draft-orders",
        label: "Embroidery",
        to: "/admin-draft-orders",
      },
      {
        id: "orders-admin/opportunities",
        label: "Opportunities",
        to: "/opportunities",
      },
      { id: "orders-admin/quotes", label: "Quotes", to: "/quotes" },
      {
        id: "orders-admin/sales-orders",
        label: "Sales Orders",
        to: "/sales-orders",
      },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    icon: "customers",
    to: "/customers",
    type: "leaf",
  },
  {
    id: "pos",
    label: "Register",
    icon: "pos",
    to: "/pos",
    type: "leaf",
  },
  {
    id: "inventory-admin",
    label: "Inventory",
    icon: "inventoryTask",
    type: "parent",
    children: [
      {
        id: "inventory-admin/stock",
        label: "Stock",
        to: "/admin/inventory/stock",
      },
      {
        id: "inventory-admin/receiving",
        label: "Receiving",
        to: "/admin/inventory/receiving",
      },
      {
        id: "inventory-admin/tasks",
        label: "Tasks",
        to: "/admin/inventory/tasks",
      },
      {
        id: "inventory-admin/purchase-orders",
        label: "Purchase orders",
        to: "/admin/inventory/purchase-orders",
      },
      {
        id: "inventory-admin/activity",
        label: "Activity",
        to: "/admin/inventory/activity",
      },
    ],
  },
  {
    id: "3pl",
    label: "3PL",
    icon: "threePl",
    type: "parent",
    children: [
      {
        id: "3pl/enrollment",
        label: "Products",
        to: "/admin/3pl/enrollment",
      },
      {
        id: "3pl/retail-stock",
        label: "Retail stock",
        to: "/admin/3pl/retail-stock",
      },
      {
        id: "3pl/sellers",
        label: "Sellers",
        to: "/admin/3pl/sellers",
      },
      {
        id: "3pl/sync-settings",
        label: "Sync settings",
        to: "/admin/3pl/sync-settings",
      },
    ],
  },
  {
    id: "inventory-seller",
    label: "Inventory",
    icon: "inventoryTask",
    type: "parent",
    children: [
      {
        id: "inventory-seller/my-stock",
        label: "My stock",
        to: "/inventory",
        // Exact: otherwise `/inventory` prefix-matches send / shipments.
        exact: true,
      },
      {
        id: "inventory-seller/send",
        label: "Send inventory",
        to: "/inventory/send",
        exact: true,
      },
      {
        id: "inventory-seller/shipments",
        label: "Shipments",
        to: "/inventory/shipments",
      },
    ],
  },
  {
    id: "manufacturing",
    label: "Manufacturing",
    icon: "factory",
    type: "parent",
    children: [
      {
        id: "manufacturing/batch-by-items",
        label: "Items",
        to: "/batch-by-items",
      },
      { id: "manufacturing/batches", label: "Batches", to: "/batches" },
      {
        id: "manufacturing/needs-labels",
        label: "Needs labels",
        to: "/production/needs-labels",
      },
      {
        id: "manufacturing/production-calendar",
        label: "Production Calendar",
        to: "/production-calendar",
      },
      {
        id: "manufacturing/tickets",
        label: "Tickets",
        to: "/tickets",
      },
      {
        id: "manufacturing/quality-control-failed-items",
        label: "QC Failed Items",
        to: "/quality-control-failed-items",
      },
      {
        id: "manufacturing/aqc-items",
        label: "AQC Items",
        to: "/aqc-items",
      },
      { id: "manufacturing/bins", label: "Bins", to: "/bins" },
      {
        id: "manufacturing/image-bins",
        label: "Image Bins",
        to: "/image-bins",
      },
      {
        id: "manufacturing/dtf-station",
        label: "DTF Station",
        to: "/dtf-station",
      },
    ],
  },
  {
    id: "production-calendar-standalone",
    label: "Production Calendar",
    icon: "calendar",
    to: "/production-calendar",
    type: "leaf",
  },
  {
    id: "settings",
    label: "Settings",
    icon: "setting",
    to: "/settings/profile",
    type: "leaf",
  },
  {
    id: "analytics",
    label: "Analytics",
    icon: "analytic",
    type: "parent",
    children: [
      {
        id: "analytics/analytics",
        label: "Analytics",
        to: "/analytics-screen",
      },
      {
        id: "analytics/storefront",
        label: "Storefront",
        to: "/analytics/storefront",
      },
      {
        id: "analytics/employee-activity",
        label: "Employee activity",
        to: "/employee-activity",
      },
      {
        id: "analytics/batched-blanks-demand",
        label: "Batched blanks demand",
        to: "/analytics/batched-blanks-demand",
      },
      {
        id: "analytics/market-watch",
        label: "Market watch",
        to: "/analytics/market-watch",
      },
    ],
  },
  {
    id: "tax",
    label: "Tax",
    icon: "tax",
    type: "parent",
    children: [
      {
        id: "tax/sales-tax",
        label: "Overview",
        to: "/tax/sales-tax",
      },
      {
        id: "tax/nexus",
        label: "Nexus",
        to: "/tax/nexus",
      },
      {
        id: "tax/resale-certificates",
        label: "Resale certificates",
        to: "/resale-certificates",
      },
    ],
  },
  {
    id: "shops",
    label: "Stores",
    icon: "shop",
    to: "/shops",
    type: "leaf",
  },
  {
    id: "seller-subscription",
    label: "Subscription",
    icon: "payment",
    to: "/subscription",
    type: "leaf",
  },
  {
    id: "payouts",
    label: "Payouts",
    icon: "payment",
    to: "/payouts",
    type: "leaf",
  },
  {
    id: "partner-payouts",
    label: "Partner Payouts",
    icon: "payment",
    to: "/partner-payouts",
    type: "leaf",
  },
];

/** NAV.1 grouped rail — enterprise admins only while rolling out. */
export const MAIN_NAV_V1 = [
  // ── 1. Dashboard ───────────────────────────────────────────────
  {
    id: "dashboard",
    label: "Dashboard",
    icon: "dashboard",
    to: "/dashboard",
    type: "leaf",
  },

  // ── 2. Orders ──────────────────────────────────────────────────
  // Two parents share the "Orders" label but never render together —
  // visibility gates make them mutually exclusive based on whether
  // the user is an enterprise admin or a seller / pro.
  {
    id: "orders-flat",
    label: "Orders",
    icon: "order",
    type: "parent",
    children: [
      { id: "orders-flat/orders", label: "Orders", to: "/orders" },
      {
        id: "orders-flat/quote-requests",
        label: "Quote requests",
        to: "/quote-requests",
      },
      { id: "orders-flat/quotes", label: "Quotes", to: "/quotes" },
      {
        id: "orders-flat/sales-orders",
        label: "Sales Orders",
        to: "/sales-orders",
      },
    ],
  },
  {
    id: "orders-admin",
    label: "Orders",
    icon: "inboxAdmin",
    type: "parent",
    children: [
      { id: "orders-admin/orders", label: "Orders", to: "/orders" },
      {
        id: "orders-admin/late-orders",
        label: "Late Orders",
        to: "/late-orders",
      },
      {
        id: "orders-admin/split-ship-pending",
        label: "Split Ship",
        to: "/split-ship-pending",
      },
      {
        id: "orders-admin/opportunities",
        label: "Opportunities",
        to: "/opportunities",
      },
      { id: "orders-admin/quotes", label: "Quotes", to: "/quotes" },
      {
        id: "orders-admin/sales-orders",
        label: "Sales Orders",
        to: "/sales-orders",
      },
    ],
  },

  // ── 2b. Register (POS.1) ───────────────────────────────────────
  // Standalone full-screen counter app. Peer of Orders / Customers —
  // front-counter job, not buried under Settings.
  {
    id: "pos",
    label: "Register",
    icon: "pos",
    to: "/pos",
    type: "leaf",
  },

  // ── 3. Customers ───────────────────────────────────────────────
  // Group keeps the name "Customers". Former top-level Customers →
  // Contacts; former top-level Inbox nests here. Leaf ids stay
  // `customers` / `inbox` so staffAccess grants keep working.
  {
    id: "customers-group",
    label: "Customers",
    icon: "customers",
    type: "parent",
    children: [
      { id: "customers", label: "Contacts", to: "/customers" },
      { id: "inbox", label: "Inbox", to: "/inbox" },
      // Admin shortcut to Fulfillment billing → Invoices (seller invoices).
      {
        id: "customers/invoices",
        label: "Invoices",
        to: "/settings/fulfillment?area=billing&tab=invoices",
        exact: true,
      },
      // Seller onboarding review queue (only while onboarding is on).
      {
        id: "customers/seller-applications",
        label: "Seller applications",
        to: "/seller-applications",
      },
    ],
  },

  // ── 4. Products ────────────────────────────────────────────────
  // Leaf ids for Catalog / Retail Products / Listings / Design library
  // keep their historical values for staffAccess continuity.
  {
    id: "products-group",
    label: "Products",
    icon: "product",
    type: "parent",
    children: [
      // FIRST position — primary product-creation flow.
      { id: "catalog", label: "Catalog", to: "/products/new-product" },
      // `exact` so /products/blanks (and other /products/*) don't light this up.
      {
        id: "products",
        label: "Retail Products",
        to: "/products",
        exact: true,
      },
      { id: "listings", label: "Listings", to: "/listings" },
      {
        id: "design-library",
        label: "Design library",
        to: "/design-library",
      },
      // NAV.1: Marketplace intentionally hidden — re-enable when supply side is ready.
      // Renamed from "Third Party Catalog". Route and page remain live (see §7).
      // { id: "marketplace", label: "Marketplace", to: "/marketplace" },
    ],
  },

  // ── 4b. Base products (seller-facing catalog foundations) ──────
  // Blanks + ready-made items sellers use to build retail products.
  // Leaf id `products/blanks` kept for staffAccess continuity.
  {
    id: "base-products-group",
    label: "Base products",
    icon: "catalog",
    type: "parent",
    children: [
      {
        id: "products/blanks",
        label: "All base products",
        to: "/products/blanks",
      },
      {
        id: "base-products/access-tiers",
        label: "Access tiers",
        to: "/catalog-tiers",
      },
      {
        id: "base-products/product-categories",
        label: "Product categories",
        to: "/product-categories",
      },
    ],
  },

  // ── 5. Manufacturing (top-level; Embroidery Files lives here) ──
  {
    id: "manufacturing",
    label: "Manufacturing",
    icon: "factory",
    type: "parent",
    children: [
      {
        id: "manufacturing/batch-by-items",
        label: "Items",
        to: "/batch-by-items",
      },
      { id: "manufacturing/batches", label: "Batches", to: "/batches" },
      {
        id: "manufacturing/needs-labels",
        label: "Needs labels",
        to: "/production/needs-labels",
      },
      {
        id: "manufacturing/production-calendar",
        label: "Production Calendar",
        to: "/production-calendar",
      },
      {
        id: "manufacturing/tickets",
        label: "Tickets",
        to: "/tickets",
      },
      // Relocated from Orders — keep leaf id for staffAccess continuity.
      {
        id: "orders-admin/admin-draft-orders",
        label: "Embroidery",
        to: "/admin-draft-orders",
      },
      {
        id: "manufacturing/quality-control-failed-items",
        label: "QC Failed Items",
        to: "/quality-control-failed-items",
      },
      {
        id: "manufacturing/aqc-items",
        label: "AQC Items",
        to: "/aqc-items",
      },
      { id: "manufacturing/bins", label: "Bins", to: "/bins" },
      {
        id: "manufacturing/image-bins",
        label: "Image Bins",
        to: "/image-bins",
      },
      {
        id: "manufacturing/dtf-station",
        label: "DTF Station",
        to: "/dtf-station",
      },
    ],
  },

  // ── 6. Inventory (top-level) ───────────────────────────────────
  {
    id: "inventory-admin",
    label: "Inventory",
    icon: "inventoryTask",
    type: "parent",
    children: [
      {
        id: "inventory-admin/stock",
        label: "Stock",
        to: "/admin/inventory/stock",
      },
      {
        id: "inventory-admin/receiving",
        label: "Receiving",
        to: "/admin/inventory/receiving",
      },
      {
        id: "inventory-admin/tasks",
        label: "Tasks",
        to: "/admin/inventory/tasks",
      },
      {
        id: "inventory-admin/purchase-orders",
        label: "Purchase orders",
        to: "/admin/inventory/purchase-orders",
      },
      {
        id: "inventory-admin/activity",
        label: "Activity",
        to: "/admin/inventory/activity",
      },
    ],
  },
  {
    id: "3pl",
    label: "3PL",
    icon: "threePl",
    type: "parent",
    children: [
      {
        id: "3pl/enrollment",
        label: "Products",
        to: "/admin/3pl/enrollment",
      },
      {
        id: "3pl/retail-stock",
        label: "Retail stock",
        to: "/admin/3pl/retail-stock",
      },
      {
        id: "3pl/sellers",
        label: "Sellers",
        to: "/admin/3pl/sellers",
      },
      {
        id: "3pl/sync-settings",
        label: "Sync settings",
        to: "/admin/3pl/sync-settings",
      },
    ],
  },
  {
    // Standalone leaf shown when Manufacturing isn't visible but the
    // user still has access to the Production Calendar (admin / pro
    // on plans where the rest of mfg is gated off).
    id: "production-calendar-standalone",
    label: "Production Calendar",
    icon: "calendar",
    to: "/production-calendar",
    type: "leaf",
  },

  // ── 7. Analytics ───────────────────────────────────────────────
  {
    id: "analytics",
    label: "Analytics",
    icon: "analytic",
    type: "parent",
    children: [
      {
        id: "analytics/analytics",
        label: "Analytics",
        to: "/analytics-screen",
      },
      {
        id: "analytics/storefront",
        label: "Storefront",
        to: "/analytics/storefront",
      },
      {
        id: "analytics/employee-activity",
        label: "Employee activity",
        to: "/employee-activity",
      },
      {
        id: "analytics/batched-blanks-demand",
        label: "Batched blanks demand",
        to: "/analytics/batched-blanks-demand",
      },
      {
        id: "analytics/market-watch",
        label: "Market watch",
        to: "/analytics/market-watch",
      },
    ],
  },

  // ── 8. Tax (enterprise admin) ───────────────────────────────────
  {
    id: "tax",
    label: "Tax",
    icon: "tax",
    type: "parent",
    children: [
      {
        id: "tax/sales-tax",
        label: "Overview",
        to: "/tax/sales-tax",
      },
      {
        id: "tax/nexus",
        label: "Nexus",
        to: "/tax/nexus",
      },
      {
        id: "tax/resale-certificates",
        label: "Resale certificates",
        to: "/resale-certificates",
      },
    ],
  },

  // ── 9. Stores (+ optional Payouts for Big Frog) ────────────────
  {
    id: "shops",
    label: "Stores",
    icon: "shop",
    to: "/shops",
    type: "leaf",
  },
  // Big Frog franchisees get paid for orders routed to them for
  // fulfillment. Surfaced as a top-level "Payouts" row gated to the
  // Big Frog network — see `payouts` in `useMainNavVisibility()`.
  {
    id: "payouts",
    label: "Payouts",
    icon: "payment",
    to: "/payouts",
    type: "leaf",
  },
  // PAY.1 — commission partners (gated in useMainNavVisibility).
  {
    id: "partner-payouts",
    label: "Partner Payouts",
    icon: "payment",
    to: "/partner-payouts",
    type: "leaf",
  },

  // ── 9. Help + Settings (pinned below divider) ──────────────────
  {
    id: "help",
    label: "Help",
    icon: "help",
    type: "leaf",
    pin: "bottom",
    external: true,
    href: ADMIN_HELP_CENTER_URL,
  },
  {
    id: "settings",
    label: "Settings",
    icon: "setting",
    to: "/settings/profile",
    type: "leaf",
    pin: "bottom",
  },

  // NAV.1: Marketplace intentionally hidden — re-enable when supply side is ready.
  // Renamed from "Third Party Catalog". Route and page remain live (see §7).
  // { id: "marketplace", label: "Marketplace", icon: "purchaseTag", to: "/marketplace", type: "leaf" },

];

/** Alias for staff-permission tree + callers that want the NAV.1 shape. */
export const MAIN_NAV = MAIN_NAV_V1;

/**
 * NAV.1 grouped IA is admin-rail only for now. Sellers keep the
 * legacy flat rail so their accounts are visually unchanged.
 */
export const useNav1Enabled = () => {
  const user = useSelector((s) => s?.currentUser?.user);
  return isAdminNavUser(user);
};

/** Active main-nav tree for the signed-in user (NAV.1 or legacy). */
export const useMainNav = () => {
  const nav1 = useNav1Enabled();
  return nav1 ? MAIN_NAV_V1 : MAIN_NAV_LEGACY;
};

/** Primary + pinned-bottom slices for the active nav tree. */
export const useMainNavSections = () => {
  const items = useMainNav();
  const nav1Enabled = useNav1Enabled();
  return useMemo(
    () => ({
      items,
      primary: items.filter((i) => i.pin !== "bottom"),
      bottom: items.filter((i) => i.pin === "bottom"),
      nav1Enabled,
    }),
    [items, nav1Enabled],
  );
};

/**
 * Per-id visibility map. `true` means render, `false` means skip.
 * Parents are skipped automatically when none of their children are
 * visible — but we still gate the parent itself for cases where the
 * whole branch should be off (e.g. `orders-admin` for non-admins).
 */
export const useMainNavVisibility = () => {
  const currentUser = useSelector((s) => s?.currentUser?.user) || {};
  const enterprise = useSelector((s) => s?.currentUser?.enterprise);
  const planId = useSelector((s) => s?.pricings?.planId);
  const enterprisePlanId = useSelector(
    (s) => activeEnterprisePlanSelector(s)?.id,
  );
  // Feature flags. `quotes.enabled` and `productionCalendar.enabled` are
  // consulted here; pulling the whole map keeps the dependency surface a
  // single hook call regardless of how many flags this resolver eventually
  // reads.
  const flags = useFeatureFlags();
  const { enabled: sellerQuoteRequestsEnabled } =
    useSellerQuoteRequestsEnabled();
  // Admin-only lookup; defaults off until the setting is known.
  const { enabled: sellerOnboardingEnabled } = useSellerOnboardingEnabled();
  // Admin only lookup; off until the server says market watch is enabled.
  const { enabled: marketWatchEnabled } = useMarketWatchEnabled();
  const sellerSubscriptionsOffered = useSellerSubscriptionsOffered();

  return useMemo(() => {
    const plantypes = getPlanTypes(enterprisePlanId) || {};
    const isNewPlan = !!plantypes.isNewPlan;
    const isOnStarterPlan = !!plantypes.isOnStarterPlan;
    const isFranchisePlan = !!plantypes.isFranchisePlan;
    const hasAdvancedInventory = plantypes.hasAdvancedInventory !== false;
    const hasNexusAccess = plantypes.hasNexusAccess !== false;
    // The `app.riverr.app` default domain has a single active production
    // account (GAME CHANGERS, contact@gamechanger.la). Manufacturing was
    // manually opened for them, but the tiered-plan gating re-hid it whenever
    // their resolved plan lands on Free/starter. Keep the whole Manufacturing
    // suite unconditionally available on this domain so a plan-logic change
    // can't silently re-gate the only user on it.
    const isRiverrDefaultDomain = isRiverrDomain;
    // Franchise plans (Big Frog) are starter-gated for sales channels but are
    // real production shops, so they keep the Manufacturing suite. Free stays
    // blocked.
    const isManufacturingVisible =
      isRiverrDefaultDomain ||
      !(isNewPlan && isOnStarterPlan) ||
      isFranchisePlan;
    const planBlocksMfg =
      !isRiverrDefaultDomain &&
      (planId === "SELLERCORE" || planId === "SELLERPLUS");
    const isAdmin = isAdminNavUser(currentUser);
    const isFulfillerOpsUser =
      (currentUser?.roles || []).includes("fulfiller_ops") ||
      currentUser?.staffAccess?.preset === "fulfiller_ops";
    const isPro = !!currentUser?.isProUser;
    // Big Frog network reach — covers the HQ domain plus every franchise
    // location (child enterprise / network split) on its own hostname, so
    // these rows show for real franchisee logins, not just HQ-domain mocks.
    const isFranchiseNetwork =
      isNetworkFranchiseMember(enterprise) || isBigfrogDomian;
    // Quotes / SO: enterprise admins only (not sellers / pro-only).
    const quotesEnabled = flags["quotes.enabled"];
    const productionCalendarEnabled = flags["productionCalendar.enabled"];
    // Messaging inbox — both sellers and admins, shown via the shared
    // `messaging.enabled` feature flag.
    const messagingEnabled = flags["messaging.enabled"];
    // Marketplace (formerly Third Party Catalog) — own flag for per-tenant
    // rollout when nav is re-enabled. Route stays live regardless.
    const marketplaceEnabled = flags["marketplace.enabled"];
    // ERP.1 — label program tenants (Walnut Creek Foods) run Batches and
    // Needs labels even though their plan hides the rest of Manufacturing.
    const erpLabelProgram = flags["erp.labelProgram"];

    const flatOrdersVisible =
      (!isAdmin || isPro || planId === "SELLERCORE") && !isPrintOnDemandDomain;
    const adminOrdersVisible = isAdmin && !isPro;
    const mfgMenuShouldRender =
      ((isAdmin || isFulfillerOpsUser) &&
        !planBlocksMfg &&
        isManufacturingVisible) ||
      (isPro && !isAdmin && !isFulfillerOpsUser && !planBlocksMfg) ||
      // ERP label program keeps a slim Manufacturing menu (Batches / Needs
      // labels) regardless of the plan gate above.
      (isAdmin && erpLabelProgram);

    // Inventory: Growth+ for pre-V4; Business+ only on V4 (advanced inventory).
    // Staging ring (Bespoke / Big Frog / localhost) keeps inventory for pre-GA work.
    // Manufacturing stays available on Growth V4.
    const inventoryAdminVisible =
      (isAdmin || isFulfillerOpsUser) &&
      !planBlocksMfg &&
      isManufacturingVisible &&
      (hasAdvancedInventory || isStagingTenant());
    // Seller warehouse program (SEL.1) — My inventory / Send stock / variances.
    // Only when the shop admin has 3PL on (inventory.3pl / features.3pl).
    const inventorySellerVisible =
      !isAdmin &&
      !isFulfillerOpsUser &&
      !isPrintOnDemandDomain &&
      !planBlocksMfg &&
      (hasAdvancedInventory || isStagingTenant()) &&
      !!flags["inventory.3pl"];
    const standaloneProductionCalendarVisible =
      (isAdmin || isPro) && !mfgMenuShouldRender;

    const catalogVisible = true;
    const productsListVisible = !isPrintOnDemandDomain;
    const listingsVisible = true;
    const designLibraryVisible = true;
    // Base products list is NAV.1 / admin-only. Pro sellers keep Blank
    // products under Settings (legacy).
    const blanksInMainNav = isAdmin && planId !== "SELLERCORE";
    const baseProductsAdminVisible =
      isAdmin && planId !== "SELLERCORE";
    const customersVisible =
      flags["customers.enabled"] && quotesEnabled && isAdmin;
    const inboxVisible = messagingEnabled;

    const base = {
      // ── Dashboard ──────────────────────────────────────────
      dashboard: true,

      // ── Orders ─────────────────────────────────────────────
      "orders-flat": flatOrdersVisible,
      "orders-flat/orders": true,
      "orders-flat/create": flatOrdersVisible,
      // Seller bulk/custom quote requests (not the admin QuoteBuilder).
      // Enterprise setting defaults on; admins can opt out in Settings.
      "orders-flat/quote-requests":
        flatOrdersVisible && sellerQuoteRequestsEnabled,
      // Quotes / SO: enterprise admins only (not sellers / pro-only).
      "orders-flat/quotes": quotesEnabled && isAdmin,
      "orders-flat/sales-orders": quotesEnabled && isAdmin,

      "orders-admin": adminOrdersVisible,
      "orders-admin/orders": true,
      // Draft, unsynced, and Shopify send failures live on My Orders
      // (Needs attention / Other), not as their own sidebar rows.
      "orders-admin/draft-orders": false,
      "orders-admin/unsynced-orders": false,
      "orders-admin/shopify-send-failures": false,
      "orders-admin/late-orders": true,
      "orders-admin/split-ship-pending": true,
      // Embroidery Files lives under Manufacturing (NAV.1); keep id for staffAccess.
      "orders-admin/admin-draft-orders":
        isAdmin && isManufacturingVisible,
      "orders-admin/opportunities":
        isBespokeThreadsDomain || isFranchiseNetwork || isLocalhost,
      "orders-admin/quotes": quotesEnabled && isAdmin,
      "orders-admin/sales-orders": quotesEnabled && isAdmin,

      // ── Register / POS.1 ───────────────────────────────────
      pos: flags["pos.enabled"] && isAdmin,

      // ── Customers (NAV.1 group + legacy leaf) ───────────────
      // Group stays open for admins when Invoices is available even if
      // Contacts / Inbox flags are off.
      "customers-group": isAdmin,
      customers: customersVisible,
      inbox: inboxVisible,
      // Seller production invoices (Fulfillment billing → Invoices).
      "customers/invoices": isAdmin,
      // Review queue: account admins, only while seller onboarding is on.
      "customers/seller-applications":
        isAdmin && !!currentUser?.isEnterpriseAdmin && sellerOnboardingEnabled,

      // ── Products (NAV.1 group + legacy leaves) ──────────────
      "products-group":
        isAdmin &&
        (catalogVisible ||
          productsListVisible ||
          listingsVisible ||
          designLibraryVisible),
      catalog: catalogVisible,
      products: productsListVisible,
      listings: listingsVisible,
      "design-library": designLibraryVisible,
      // Marketplace: flag-gated; nav entry commented out in MAIN_NAV.
      marketplace: marketplaceEnabled && isAdmin,
      // Legacy id — kept false so old staff grants don't resurrect a row.
      "third-party-catalog": false,

      // ── Base products (NAV.1 group) ─────────────────────────
      "base-products-group":
        isAdmin &&
        (blanksInMainNav || baseProductsAdminVisible),
      "products/blanks": blanksInMainNav,
      "base-products/access-tiers": baseProductsAdminVisible,
      "base-products/product-categories": baseProductsAdminVisible,

      // ── Inventory (top-level) ──────────────────────────────
      "inventory-admin": inventoryAdminVisible,
      "inventory-admin/stock": true,
      "inventory-admin/receiving": true,
      "inventory-admin/tasks": true,
      "inventory-admin/purchase-orders": true,
      "inventory-admin/activity": true,

      // INV.1 — 3PL group (absent when inventory.3pl is off)
      "3pl": inventoryAdminVisible && !!flags["inventory.3pl"],
      "3pl/enrollment": true,
      "3pl/retail-stock": true,
      "3pl/sellers": true,
      "3pl/sync-settings": true,

      // Absent when admin 3PL is off — sellers should not see Inventory.
      "inventory-seller": inventorySellerVisible,
      "inventory-seller/my-stock": true,
      "inventory-seller/send": true,
      "inventory-seller/shipments": true,

      // ── Manufacturing (top-level) ──────────────────────────
      manufacturing: mfgMenuShouldRender,
      "manufacturing/tickets":
        isAdmin && isManufacturingVisible && flags["tickets.rail"],
      "manufacturing/batch-by-items": isAdmin && isManufacturingVisible,
      "manufacturing/batches":
        isAdmin && (isManufacturingVisible || erpLabelProgram),
      // ERP queue only. Walnut Creek Foods via erp.labelProgram; other
      // shops keep Batches without this row.
      "manufacturing/needs-labels": isAdmin && !!erpLabelProgram,
      "manufacturing/bins": isAdmin && isManufacturingVisible,
      "manufacturing/image-bins": isAdmin && isManufacturingVisible,
      "manufacturing/quality-control-failed-items":
        isAdmin && isManufacturingVisible,
      "manufacturing/aqc-items":
        isAdmin &&
        isManufacturingVisible &&
        (isPrintifyDomain || isBespokeThreadsDomain),
      "manufacturing/production-calendar": productionCalendarEnabled,

      "production-calendar-standalone":
        productionCalendarEnabled && standaloneProductionCalendarVisible,

      // ── Manage / explore ──────────────────────────────────
      // Sellers (non-admins) get a Settings row in the main rail so
      // they can reach Profile / Billing / Share access without the
      // avatar flyout. Enterprise admins keep their existing plan gate.
      settings:
        !isAdmin ||
        (planId !== "SELLERCORE" && planId !== "SELLERPLUS"),
      // Admin help center — enterprise admins only (sellers keep
      // helpDeskLink / topbar Help for their branded seller desk).
      help: isAdmin,
      analytics: true,
      // HQ super-admins use Storefront (shop.bigfrog.com). Franchise
      // locations and every other tenant keep the production Analytics page.
      "analytics/analytics": !isBigFrogHqSuperAdmin(enterprise, currentUser),
      // Big Frog / network HQ + franchise child admins — storefront conversion.
      "analytics/storefront": (() => {
        if (!isAdmin) return false;
        const entId = enterprise?.id || enterprise?.enterpriseId;
        const isNetworkParent =
          !enterprise?.parentEnterpriseId &&
          (enterprise?.paymentSplitModel === "network" ||
            entId === BIG_FROG_ENTERPRISE_ID ||
            isBigfrogDomian);
        if (isNetworkParent) {
          const roles = Array.isArray(currentUser?.roles)
            ? currentUser.roles
            : [];
          return roles.includes("super_admin") || !currentUser?.locationId;
        }
        // Franchise location (child enterprise) admins.
        return (
          !!enterprise?.parentEnterpriseId &&
          (isFranchiseNetwork || entId === BIG_FROG_ENTERPRISE_ID)
        );
      })(),
      // Same gate as the former Settings → Operations leaf.
      "analytics/employee-activity":
        isAdmin && canInviteStaff(currentUser),
      // Inventory planning from historical production batches.
      "analytics/batched-blanks-demand": isAdmin,
      // Etsy trends; only accounts with market watch turned on (House Label).
      "analytics/market-watch": isAdmin && marketWatchEnabled,
      tax: isAdmin,
      "tax/nexus": isAdmin && hasNexusAccess,
      "tax/sales-tax": isAdmin,
      "tax/resale-certificates": isAdmin,
      shops: !isPrintifyDomain,
      // Bespoke: hidden until an admin turns a plan on.
      // OmniCommerce: one seller account, even before a plan is on.
      "seller-subscription":
        !isAdmin &&
        ((isBespokeThreadsDomain && sellerSubscriptionsOffered) ||
          (isOmniCommerceContext(enterprise) &&
            isAllowlistedOmniSellerSubscriptionEmail(currentUser?.email))),
      // Payouts: Big Frog franchisees only. Mirrors the audience that
      // currently reaches the page via Settings → Billing (enterprise
      // admins), scoped to the Big Frog domain.
      payouts: isFranchiseNetwork && isAdmin,
      // PAY.1 — Partner Payouts: program enabled on enterprise (partners may
      // still be empty — roster is how you create the first one).
      "partner-payouts":
        isAdmin && !!enterprise?.commissionProgram?.enabled,
    };

    // Custom staffAccess narrows the rail further (Shopify-style scopes).
    const next = applySellerPermissionVisibility(
      currentUser,
      applySharedAccessVisibility(
        currentUser,
        applyStaffPermissionVisibility(currentUser, base),
      ),
    );

    // NAV.1 group headers compose from children (admin rail only).
    // Staff grants still use legacy leaf ids (`inbox`, `catalog`,
    // `manufacturing/…`), which do not share a `customers-group/`
    // prefix — so re-derive after the staff pass for admins.
    if (isAdmin) {
      next["customers-group"] =
        next.customers !== false ||
        next.inbox !== false ||
        next["customers/invoices"] !== false ||
        next["customers/seller-applications"] !== false;
      next["products-group"] =
        next.catalog !== false ||
        next.products !== false ||
        next.listings !== false ||
        next["design-library"] !== false;
      next["base-products-group"] =
        next["products/blanks"] !== false ||
        next["base-products/access-tiers"] !== false ||
        next["base-products/product-categories"] !== false;
      // Manufacturing parent must stay open when Embroidery Files
      // (legacy `orders-admin/admin-draft-orders` id) is the only grant.
      if (mfgMenuShouldRender) {
        next.manufacturing =
          next.manufacturing !== false ||
          next["orders-admin/admin-draft-orders"] !== false ||
          next["manufacturing/tickets"] !== false ||
          next["manufacturing/batch-by-items"] !== false ||
          next["manufacturing/batches"] !== false ||
          next["manufacturing/needs-labels"] !== false ||
          next["manufacturing/bins"] !== false ||
          next["manufacturing/image-bins"] !== false ||
          next["manufacturing/quality-control-failed-items"] !== false ||
          next["manufacturing/aqc-items"] !== false ||
          next["manufacturing/production-calendar"] !== false;
      }
    } else {
      next["customers-group"] = false;
      next["products-group"] = false;
      next["base-products-group"] = false;
      next["products/blanks"] = false;
      next["base-products/access-tiers"] = false;
      next["base-products/product-categories"] = false;
    }

    return next;
  }, [
    currentUser,
    enterprise,
    planId,
    enterprisePlanId,
    flags,
    sellerQuoteRequestsEnabled,
    sellerOnboardingEnabled,
    marketWatchEnabled,
    sellerSubscriptionsOffered,
  ]);
};

/**
 * Resolve a parent + visibility map down to its visible children. Used
 * by the renderers to decide whether the parent itself should render
 * (skip if no visible children) and which child rows to emit.
 * Nested parents are kept only when they themselves have visible kids.
 */
export const visibleChildrenOf = (parent, visibility) =>
  (parent.children || []).filter((c) => {
    if (visibility[c.id] === false) return false;
    if (c.type === "parent") {
      return visibleChildrenOf(c, visibility).length > 0;
    }
    return true;
  });

/** Strip query/hash so `to` values with `?tab=` still match pathname. */
const navPathnameOnly = (path) => {
  if (!path) return path;
  const cut = path.search(/[?#]/);
  return cut === -1 ? path : path.slice(0, cut);
};

/** Whether a path matches a nav `to` (exact or nested under it). */
export const navPathMatches = (linkPath, currentPath, { exact } = {}) => {
  if (!linkPath || linkPath === "#" || !currentPath) return false;
  const path = navPathnameOnly(linkPath);
  if (!path || path === "#") return false;
  if (path === currentPath) return true;
  if (exact) return false;
  return currentPath.startsWith(`${path}/`);
};

/**
 * Ancestor parent ids that contain a leaf matching `pathname`.
 * Used to auto-expand collapsed groups on direct navigation.
 */
export const findOpenAncestorsForPath = (items, pathname, ancestors = []) => {
  const found = [];
  for (const item of items || []) {
    if (item.type === "parent") {
      const nextAncestors = [...ancestors, item.id];
      for (const child of item.children || []) {
        if (child.type === "parent") {
          found.push(
            ...findOpenAncestorsForPath([child], pathname, nextAncestors),
          );
        } else if (navPathMatches(child.to, pathname, { exact: child.exact })) {
          found.push(...nextAncestors);
        }
      }
    } else if (
      navPathMatches(item.to, pathname, { exact: item.exact }) &&
      ancestors.length
    ) {
      found.push(...ancestors);
    }
  }
  return found;
};

/** Recursively collect every leaf id reachable from a nav tree. */
const collectLeafIds = (items) =>
  (items || []).flatMap((item) => {
    if (item.type === "parent") {
      return (item.children || []).flatMap((c) =>
        c.type === "parent" ? collectLeafIds([c]) : [c.id],
      );
    }
    return [item.id];
  });

/** Flat list of every leaf id reachable from NAV.1 (admin) tree. */
export const MAIN_NAV_LEAF_IDS = collectLeafIds(MAIN_NAV_V1);

/** @deprecated Prefer `useMainNavSections()` — these are NAV.1-only. */
export const MAIN_NAV_PRIMARY = MAIN_NAV_V1.filter((i) => i.pin !== "bottom");

/** @deprecated Prefer `useMainNavSections()` — these are NAV.1-only. */
export const MAIN_NAV_BOTTOM = MAIN_NAV_V1.filter((i) => i.pin === "bottom");
